import { createFileRoute } from "@tanstack/react-router";
import { ogImageMeta } from "@/lib/seo/og-image";
import { LocaleLink } from "@/components/site/LocaleLink";
import { ArrowRight, Compass } from "lucide-react";
import { FaqSearch } from "@/components/site/FaqSearch";
import { Button } from "@/components/ui/button";
import { SubPagePillNav, MEDIA_ANALYSIS_PILL_ITEMS_BG } from "@/components/site/SubPagePillNav";
import { MediaAnalysisHero } from "@/components/site/MediaAnalysisHero";
import { Reveal } from "@/components/site/Reveal";
import { mediaAnalysisFaqsBg } from "@/content/faqs";

import { pageSchemas } from "@/lib/seo/page-schema";
const META_DESCRIPTION =
  "Често задавани въпроси за медийните анализи на Perceptica - методология, резултати, срокове, езици и цени. Прочетете отговорите.";

export const Route = createFileRoute("/bg/media-analysis/faq")({
  head: () => ({
    meta: [
      { title: "Медиен анализ - често задавани въпроси | Perceptica" },
      { name: "description", content: META_DESCRIPTION },
      { property: "og:title", content: "Медиен анализ - често задавани въпроси | Perceptica" },
      { property: "og:description", content: META_DESCRIPTION },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Медиен анализ - често задавани въпроси | Perceptica" },
      { name: "twitter:description", content: META_DESCRIPTION },
      { property: "og:url", content: "https://perceptica.com/bg/media-analysis/faq" },
      ...ogImageMeta("bg", "/media-analysis/faq"),
    ],
  
    scripts: pageSchemas({ locale: "bg", path: "/media-analysis/faq", faq: mediaAnalysisFaqsBg }),
  }),
  component: MediaAnalysisFaqPageBg,
});

function MediaAnalysisFaqPageBg() {
  return (
    <div>
      <MediaAnalysisHero
        eyebrow="Често задавани въпроси за медиен анализ"
        headlineLead="Често задавани въпроси за "
        headlineHighlight="медийния анализ"
        description="Как работи анализът, кой го извършва, какво получавате и как се различава от платформата за медиен мониторинг."
        ctas={[
          { label: "Свържете се с нас", to: "/request-a-demo" },
          { label: "Разгледайте въпросите", href: "#faqs" },
        ]}
      />
      <SubPagePillNav items={MEDIA_ANALYSIS_PILL_ITEMS_BG} />

      <section id="faqs" className="scroll-mt-32 bg-background py-24 lg:py-32">
        <div className="mx-auto max-w-[1100px] px-6 lg:px-10">
          <Reveal>
            <div className="flex flex-col items-start gap-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
                Въпроси и отговори
              </p>
              <h2 className="font-display max-w-3xl text-[clamp(1.85rem,3vw,2.5rem)] leading-[1.1] text-foreground">
                Всичко, което трябва да знаете за медийния анализ
              </h2>
              <p className="mt-2 max-w-2xl text-[15px] leading-relaxed text-foreground/70">
                Потърсете сред най-честите въпроси или се свържете директно с нас, ако искате да говорите със старши анализатор.
              </p>
            </div>
          </Reveal>

          <div className="mt-12">
            <FaqSearch items={mediaAnalysisFaqsBg} idPrefix="ma-faq-bg" placeholder="Търсене във въпросите..." />
          </div>
        </div>
      </section>

      <section className="relative bg-background pt-20 pb-28 lg:pt-24 lg:pb-32">
        <div className="mx-auto max-w-[1280px] px-6 lg:px-10">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl bg-primary text-primary-foreground shadow-[0_30px_80px_-30px_rgba(15,12,40,0.5)]">
              <div className="grid-overlay" />
              <div className="noise-overlay" />
              <div
                aria-hidden
                className="pointer-events-none absolute -top-24 -left-24 h-[360px] w-[360px] rounded-full opacity-40 blur-[120px]"
                style={{ background: "radial-gradient(circle, #6B5BF5 0%, transparent 70%)" }}
              />
              <div
                aria-hidden
                className="pointer-events-none absolute -bottom-32 -right-24 h-[380px] w-[380px] rounded-full opacity-40 blur-[120px]"
                style={{ background: "radial-gradient(circle, #FF2D78 0%, transparent 70%)" }}
              />
              <div className="relative grid gap-12 px-8 py-16 lg:grid-cols-[1.4fr_1fr] lg:items-center lg:gap-16 lg:px-16 lg:py-20">
                <div>
                  <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/80">
                    <Compass className="h-3.5 w-3.5 text-accent" />
                    Говорете със старши анализатор
                  </span>
                  <h2 className="font-display mt-6 max-w-[22ch] text-[clamp(1.85rem,3.2vw,2.75rem)] leading-[1.05] text-white">
                    Имате още въпроси за медийния анализ?
                  </h2>
                  <p className="mt-6 max-w-xl text-[15.5px] leading-relaxed text-white/70">
                    Разкажете ни за вашите цели и ще препоръчаме подходящата аналитична програма - еднократна или текуща.
                  </p>
                </div>
                <div className="flex flex-col gap-4 lg:items-end">
                  <Button asChild size="xl" className="w-full lg:w-auto">
                    <LocaleLink to="/request-a-demo">
                      Нека поговорим
                      <ArrowRight className="h-4 w-4" />
                    </LocaleLink>
                  </Button>
                  <p className="text-[12px] uppercase tracking-[0.18em] text-white/80">
                    На повечето запитвания отговаряме в рамките на един работен ден.
                  </p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}